import { z } from "zod";
import { GMS_ROLES, type GmsRole } from "./auth";
import { paginationQuery } from "./pagination";

/**
 * Change Logs (user, 2026-10-28) — what changed in each app, in words a
 * client understands ("Fixed photos not showing on the records page").
 *
 * The entries live in the repo (`apps/api/src/changelog/`), written in the
 * same commit as the change and the app's `package.json` version bump; the
 * API only serves them. Versions are SemVer per app, from 0.0.1: a new
 * feature bumps the minor (0.1.0), a fix only the patch (0.0.2); 1.0.0 the
 * day the Games go live. A unit test holds every app's latest entry equal to
 * its package.json version.
 *
 * Who sees the page is ONE setting (`changelog_settings.visible_roles`,
 * SUPER_ADMIN edits): the GMS roles listed there see GMS's own log;
 * SUPER_ADMIN always sees it, and is the only role that sees the other apps'.
 */

/** Every app with a changelog — `apps/*`, by folder name. GMS first: the page's default. */
export const CHANGELOG_APPS = ["gms", "api", "ovr", "mses", "tsr", "tsr-desktop", "wsl-notion", "web-portal"] as const;
export type ChangelogApp = (typeof CHANGELOG_APPS)[number];
export const CHANGELOG_DEFAULT_APP: ChangelogApp = "gms";

/** What the app is called on the page (product names, never translated). */
export const CHANGELOG_APP_NAMES: Record<ChangelogApp, string> = {
	gms: "GMS",
	api: "API",
	ovr: "OVR",
	mses: "MSES",
	tsr: "TSR",
	"tsr-desktop": "TSR Desktop",
	"wsl-notion": "WSL Notion",
	"web-portal": "Portal",
};

/** A change line's chip. Values, never keys (`ok()` lower-cases all-caps keys). */
export const CHANGELOG_CHANGE_KINDS = ["NEW", "IMPROVED", "FIXED"] as const;
export type ChangelogChangeKind = (typeof CHANGELOG_CHANGE_KINDS)[number];

/** A client reads these: one short sentence each. Held by the unit test. */
export const CHANGELOG_CHANGE_TEXT_MAX = 160;

/** The role that always sees the page (it cannot hide it from itself) and the only one that sees every app's log. */
export const CHANGELOG_ALL_APPS_ROLE = "SUPER_ADMIN" as const satisfies GmsRole;
/** The roles the setting can list: those that can reach the GMS at all. */
export const CHANGELOG_ROLES = GMS_ROLES;
export const CHANGELOG_REASON_MAX = 500;

export const SEMVER_RE = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

export interface ChangelogChange {
	kind: ChangelogChangeKind;
	text: string;
}

export interface ChangelogRelease {
	/** SemVer, no "v" — the page draws the "v". */
	version: string;
	/** The release's date, `YYYY-MM-DD` (not a moment: shown as written, in no time zone). */
	date: string;
	changes: ChangelogChange[];
}

export const changelogQuery = paginationQuery.extend({
	app: z.enum(CHANGELOG_APPS).default(CHANGELOG_DEFAULT_APP),
	page_size: z.coerce.number().int().min(1).max(50).default(10),
});
export type ChangelogQueryInput = z.input<typeof changelogQuery>;

/** `GET /changelog` — one app's releases, newest first, offset paged. */
export interface ChangelogPage {
	app: ChangelogApp;
	current_version: string;
	/** The apps this session may pick (`["gms"]` for everyone but SUPER_ADMIN), each with its current version. */
	apps: { app: ChangelogApp; current_version: string }[];
	releases: ChangelogRelease[];
	page: number;
	page_size: number;
	total: number;
	total_pages: number;
}

/** `GET /changelog/settings` (SUPER_ADMIN). */
export interface ChangelogSettings {
	visible_roles: GmsRole[];
	updated_at: string;
}

export const putChangelogSettingsBody = z.object({
	visible_roles: z.array(z.enum(CHANGELOG_ROLES)).max(CHANGELOG_ROLES.length),
	reason: z.string().trim().min(1).max(CHANGELOG_REASON_MAX),
});
export type PutChangelogSettingsBody = z.input<typeof putChangelogSettingsBody>;
